import { useState } from 'react';
import { Outlet } from 'react-router-dom';

import { Label } from '@/components/ui/catalyst/fieldset';
import { Switch, SwitchField } from '@/components/ui/catalyst/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTabNavigation } from '@/hooks/useTabNavigation';

const VALID_TABS = ['overview', 'timeline', 'check-ins'] as const;
export type ViewMode = (typeof VALID_TABS)[number];

const TREATMENT_NEW_DESIGN_KEY = 'treatment-new-design-enabled-v3';

export const TreatmentOverviewPage = () => {
  const { activeTab, handleTabChange } = useTabNavigation<ViewMode>({
    validTabs: VALID_TABS,
    defaultTab: 'overview',
  });

  // New design toggle state - persisted to localStorage
  const [useNewDesign, setUseNewDesign] = useState(() => {
    try {
      const stored = localStorage.getItem(TREATMENT_NEW_DESIGN_KEY);
      return stored !== 'false';
    } catch {
      return true;
    }
  });

  const handleToggleDesign = (checked: boolean) => {
    setUseNewDesign(checked);
    localStorage.setItem(TREATMENT_NEW_DESIGN_KEY, String(checked));
  };

  return (
    <div className="px-6 py-6 h-full overflow-y-auto">
      <div className="w-full mx-auto min-h-full flex flex-col">
        {/* Header with tabs (hidden in new design) and design toggle */}
        <div className="flex items-center justify-between mb-4 shrink-0">
          {!useNewDesign ? (
            <Tabs
              value={activeTab}
              onValueChange={(value) => handleTabChange(value as ViewMode)}
              className="w-full"
            >
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="timeline">Timeline View</TabsTrigger>
                <TabsTrigger value="check-ins">Treatment Check-ins</TabsTrigger>
              </TabsList>
            </Tabs>
          ) : (
            <div /> /* Empty div for flex spacing */
          )}

          {/* Design Toggle - always visible */}
          <SwitchField className="flex-shrink-0 gap-x-0 items-center justify-center">
            <Label className="text-sm text-zinc-600 cursor-pointer">
              New Design
            </Label>
            <Switch
              color="indigo-steel"
              checked={useNewDesign}
              onChange={handleToggleDesign}
            />
          </SwitchField>
        </div>

        <div className="flex-1">
          <Outlet context={{ useNewDesign }} />
        </div>
      </div>
    </div>
  );
};
